import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import api from "../lib/api";
import { statusTone, toneClasses } from "../lib/commandCenterStatusTone";
import { disclaimerRedirect } from "../lib/disclaimerGate";
import { useAuth } from "../context/AuthContext";
import OnboardingChecklist from "../components/OnboardingChecklist";
import { toast } from "sonner";
import {
  ShieldAlert, ShieldCheck, AlertTriangle, Clock, CheckCircle2,
  ChevronRight, Lock, Flag, Phone, Hash, FileWarning, AlertOctagon,
} from "lucide-react";

const RESPONSIBLE_LABELS = {
  client:             "You",
  echosense:          "EchoSense Team",
  kyc_provider:       "KYC Provider",
  accreditation:      "Accreditation Reviewer",
  regulated_partner:  "Regulated Partner",
  compliance:         "EchoSense Compliance",
};

const CommandCenterPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [escalateReason, setEscalateReason] = useState("");
  const [escalateDetail, setEscalateDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const r = await api.get("/command-center/me");
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load your command center");
    } finally {
      setLoading(false);
    }
  };

  const submitEscalation = async () => {
    if (!escalateReason) { toast.error("Please select a reason"); return; }
    setSubmitting(true);
    try {
      await api.post("/command-center/escalate", { reason: escalateReason, detail: escalateDetail });
      toast.success("Your file has been escalated for priority review.");
      setEscalateOpen(false);
      setEscalateReason(""); setEscalateDetail("");
      await fetchData();
    } catch (e) {
      toast.error("Could not raise escalation. Please contact support.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !data) {
    return (
      <DashboardLayout title="Command Center" subtitle="Loading your file...">
        <div className="animate-pulse h-32 bg-slate-100 rounded-lg" />
      </DashboardLayout>
    );
  }

  // STARSEED-CAPMARKETS-257 — registration signs the member in and lands
  // them HERE, not on /dashboard, so "the disclaimer is acknowledged at
  // first login" has to hold on this page too. Same predicate as the
  // dashboard (`lib/disclaimerGate.js`); the member returns here afterwards.
  const gate = disclaimerRedirect(user, data.onboarding_checklist, "/command-center");
  if (gate) {
    return <Navigate to={gate} replace />;
  }

  const tone = statusTone(data.status);
  const progressPct = data.completed_steps.length && (data.completed_steps.length + data.missing_steps.length)
    ? Math.round((data.completed_steps.length / (data.completed_steps.length + data.missing_steps.length)) * 100)
    : 0;
  const responsible = RESPONSIBLE_LABELS[data.responsible_party] || data.responsible_party;

  return (
    <DashboardLayout title="Command Center" subtitle="Your onboarding operating system">
      {/* DO-NOT-SEND-FUNDS — always visible at top */}
      <div
        data-testid="do-not-send-funds-banner"
        className="mb-6 p-4 rounded-lg border-2 border-red-300 bg-red-50 flex gap-3 items-start"
      >
        <AlertOctagon className="text-red-700 shrink-0 mt-0.5" size={22} />
        <div>
          <p className="text-red-900 font-semibold text-sm uppercase tracking-wide">Do not send funds</p>
          <p className="text-red-900 text-sm mt-1 leading-relaxed">{data.do_not_send_funds_notice}</p>
        </div>
      </div>

      {/* RED ALERT BANNER */}
      {data.red_alert && (
        <div data-testid="red-alert-banner" className="mb-6 p-5 rounded-lg border-2 border-red-500 bg-red-100">
          <div className="flex gap-3 items-start">
            <ShieldAlert className="text-red-700 shrink-0 mt-1" size={28} />
            <div className="flex-1">
              <p className="text-red-900 font-bold text-base uppercase tracking-wide">Priority Review In Progress</p>
              <p className="text-red-900 text-sm mt-2 leading-relaxed">
                Your file has been marked for priority review. An EchoSense team member will review your onboarding
                status and determine the appropriate next step. Please do not send funds or rely on verbal instructions.
                All official next steps will be provided through the portal or approved written communication.
              </p>
              {data.red_alert_reasons?.length > 0 && (
                <div className="mt-3 p-3 bg-white/50 rounded border border-red-200">
                  <p className="text-xs font-semibold text-red-900 uppercase tracking-wide mb-1">Reason</p>
                  <ul className="text-xs text-red-900 space-y-1">
                    {data.red_alert_reasons.map((r, i) => <li key={`${r}-${i}`}>• {r}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* COMPLIANCE ATTENTION BANNER */}
      {!data.red_alert && data.compliance_attention_required && (
        <div data-testid="compliance-banner" className="mb-6 p-4 rounded-lg border-2 border-amber-400 bg-amber-50 flex gap-3 items-start">
          <FileWarning className="text-amber-700 shrink-0 mt-0.5" size={22} />
          <div>
            <p className="text-amber-900 font-semibold">Compliance Attention Required</p>
            <p className="text-amber-900 text-sm mt-1">
              Based on your answers, your file requires additional review before proceeding.
              EchoSense does not recommend that anyone use emergency funds, living-expense money, payroll funds,
              borrowed funds, or required living capital for speculative or investment-related opportunities.
            </p>
          </div>
        </div>
      )}

      {/* STATUS HERO */}
      <Card className={`mb-6 border-2 ${toneClasses(tone.tone)}`}>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-70">
                <Hash size={14} /> EchoSense VC Number
              </div>
              <p data-testid="vc-number" className="text-2xl font-mono font-bold mt-1">{data.vc_number}</p>
              {data.legacy_vc && (
                <p className="text-xs opacity-60 mt-1">Introducer chain: <span className="font-mono">{data.legacy_vc}</span></p>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              {/* capmarkets-140: the member-facing LABEL, never `data.status`.
                  The raw status carries internal vocabulary ("FinBPM Approved"),
                  which member-facing copy is vendor-neutral about everywhere
                  else. Pinned by commandCenterBadgeWiring.test.jsx. */}
              <Badge data-testid="status-badge" className={`${toneClasses(tone.tone)} border text-sm px-3 py-1`}>
                {tone.label}
              </Badge>
              {data.is_stalled && (
                <Badge data-testid="stalled-badge" className="bg-amber-200 text-amber-900 border border-amber-400">
                  <Clock size={12} className="mr-1" /> Stalled
                </Badge>
              )}
              {data.is_priority_stalled && (
                <Badge className="bg-red-200 text-red-900 border border-red-400">
                  <Clock size={12} className="mr-1" /> Priority Stalled
                </Badge>
              )}
              {data.is_high_value && (
                <Badge className="bg-violet-100 text-violet-900 border border-violet-300">High Value</Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ONBOARDING CHECKLIST — canonical Step-1 items with deep links
          (onboarding-gate-004; data from gate-001) */}
      <OnboardingChecklist items={data.onboarding_checklist}
        agreementInFlight={data.agreement_in_flight}
        agreementStatus={data.status} />

      {/* PROGRESS + NEXT STEP */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="lg:col-span-2 bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900">Where you are in the process</CardTitle>
            <CardDescription className="text-slate-600">
              {data.completed_steps.length} of {data.completed_steps.length + data.missing_steps.length} milestones complete
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Progress value={progressPct} className="mb-6" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-2">Completed</p>
                {data.completed_steps.length === 0 && <p className="text-sm text-slate-500">None yet</p>}
                {data.completed_steps.map((s, i) => (
                  <div key={`done-${s}-${i}`} className="flex items-center gap-2 text-sm text-slate-900 py-1">
                    <CheckCircle2 size={16} className="text-emerald-600" /> {s}
                  </div>
                ))}
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-amber-700 font-semibold mb-2">Missing</p>
                {data.missing_steps.length === 0 && <p className="text-sm text-slate-500">All steps complete</p>}
                {data.missing_steps.map((s, i) => (
                  <div key={`miss-${s}-${i}`} className="flex items-center gap-2 text-sm text-slate-700 py-1">
                    <Clock size={16} className="text-amber-600" /> {s}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 text-white border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Next required action</CardTitle>
            <CardDescription className="text-slate-300">
              Responsible: <span className="font-semibold text-white">{responsible}</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed mb-4">{data.next_action}</p>
            <div className="space-y-2 mb-4 text-xs">
              <div className="flex justify-between border-b border-white/10 pb-1">
                <span className="text-slate-400">Blocked on</span>
                <span className="text-white font-medium">{data.blocked_on || "—"}</span>
              </div>
              <div className="flex justify-between border-b border-white/10 pb-1">
                <span className="text-slate-400">Priority</span>
                <span className="text-white font-medium uppercase">{data.priority_level}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">KYC</span>
                <span className="text-white font-medium">{data.kyc_status}</span>
              </div>
            </div>
            {data.next_action_url && data.next_action_label && (
              <Button
                data-testid="next-action-btn"
                onClick={() => navigate(data.next_action_url)}
                className="w-full bg-white text-slate-900 hover:bg-slate-100 font-semibold"
              >
                {data.next_action_label} <ChevronRight size={16} />
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* INTAKE CTA */}
      {!data.intake_complete && (
        <Card className="mb-6 border-blue-300 bg-blue-50">
          <CardContent className="p-5 flex items-center gap-4">
            <Flag className="text-blue-700 shrink-0" size={28} />
            <div className="flex-1">
              <p className="font-semibold text-blue-900">Onboarding intake required</p>
              <p className="text-sm text-blue-900/80 mt-1">
                Complete a short capital readiness and risk-readiness questionnaire so EchoSense can route your file correctly.
                This is not investment advice — it's a suitability and compliance intake.
              </p>
            </div>
            <Button
              data-testid="start-intake-btn"
              onClick={() => navigate("/command-center/intake")}
              className="bg-blue-700 text-white hover:bg-blue-800 shrink-0"
            >
              Start Intake <ChevronRight size={16} />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* CLIENT-FACING ADMIN NOTES */}
      {data.client_facing_notes?.length > 0 && (
        <Card className="mb-6 bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <Flag size={18} className="text-amber-600" /> Messages from EchoSense
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.client_facing_notes.map((n, i) => (
                <div key={n.id || i} className="p-3 bg-amber-50 border border-amber-200 rounded">
                  <p className="text-sm text-slate-900">{n.message}</p>
                  <p className="text-xs text-slate-500 mt-1">{n.created_at}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* WHAT NOT TO DO + PORTAL PURPOSE */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2 text-base">
              <ShieldCheck size={18} className="text-blue-700" /> What this portal is
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-700 leading-relaxed">{data.portal_purpose_statement}</p>
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2 text-base">
              <Lock size={18} className="text-red-700" /> What you should not do
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-sm text-slate-700 space-y-2 leading-relaxed">
              <li>• Do not send funds based on verbal instructions.</li>
              <li>• Do not act on any "guaranteed return" claim.</li>
              <li>• Do not bypass written communication from the regulated partner.</li>
              <li>• Do not use emergency funds, payroll, or living-expense money.</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* ESCALATE */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-5 flex flex-col md:flex-row md:items-center gap-4">
          <AlertTriangle className="text-amber-600 shrink-0" size={28} />
          <div className="flex-1">
            <p className="font-semibold text-slate-900">Something doesn't look right?</p>
            <p className="text-sm text-slate-600 mt-1">
              If you see anything confusing, concerning, or off — escalate your file. EchoSense compliance will review immediately.
            </p>
          </div>
          <Button
            data-testid="escalate-btn"
            onClick={() => setEscalateOpen(true)}
            variant="outline"
            className="border-amber-400 text-amber-900 hover:bg-amber-50"
          >
            <Flag size={16} className="mr-2" /> Escalate File
          </Button>
        </CardContent>
      </Card>

      {/* Escalate dialog */}
      <Dialog open={escalateOpen} onOpenChange={setEscalateOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Escalate to EchoSense compliance</DialogTitle>
            <DialogDescription className="text-slate-600">
              Your file will be marked for priority review. Please select a reason and provide any helpful detail.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-slate-900">Reason</Label>
              <select
                data-testid="escalate-reason"
                value={escalateReason}
                onChange={(e) => setEscalateReason(e.target.value)}
                className="w-full mt-1 p-2 border border-slate-300 rounded bg-white text-slate-900"
              >
                <option value="">Select a reason...</option>
                <option>I am confused about where to send money</option>
                <option>Someone promised me a return</option>
                <option>I was told to act fast</option>
                <option>I was given verbal funding instructions</option>
                <option>My file appears stuck</option>
                <option>I need urgent guidance</option>
                <option>Other concern</option>
              </select>
            </div>
            <div>
              <Label className="text-slate-900">Detail (optional)</Label>
              <Textarea
                data-testid="escalate-detail"
                value={escalateDetail}
                onChange={(e) => setEscalateDetail(e.target.value)}
                rows={4}
                className="mt-1 bg-white text-slate-900 border-slate-300"
                placeholder="Describe what happened or what concerns you..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEscalateOpen(false)}>Cancel</Button>
            <Button
              data-testid="submit-escalate-btn"
              onClick={submitEscalation}
              disabled={submitting}
              className="bg-red-700 text-white hover:bg-red-800"
            >
              {submitting ? "Submitting..." : "Submit Escalation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default CommandCenterPage;
